// src/hooks/useAiWeatherIntelligence.js
// --- UPDATE: new hook — AI weather + flood intelligence for the live readings page
// ============================================================================
// • Polls Open-Meteo for the asset-field centroid (cached, de-duplicated, paused
//   while the tab is hidden) and accepts server-pushed weather over WebSocket
//   ('weather_update' / 'ai_weather_forecast'), which wins while fresh.
// • Runs the edge flood model for every well that has water-level data.
// • Merges with server ML predictions (WebSocket 'ai_flood_prediction' and any
//   backend-enriched well.ai_predictions). Server predictions ALWAYS win while
//   fresh; the edge model only fills gaps, and is labelled source: 'edge'.
// • Keeps prediction object identity stable when nothing material changed, so
//   memoised table rows don't re-render on every telemetry tick.
// --- UPDATE (weather v2)
// • Weather comes from the FlowSense server first (GET /simulations/weather/now — several weather models +
//   staff rain reports, the same numbers the drainage outlook and road ponding forecast use), every 5 min and
//   on every `weather_update` push. Direct Open-Meteo is only the fallback (older server / server down).
// • The newest reading wins (it used to be: any server push beats a fresher poll for a whole hour).
// • Location: centre of ALL mapped assets (it was the centre of the wells with telemetry — one well).
// • reportRain(intensity): staff "it is raining here" report → updates the card, the outlook and the roads.
// --- END UPDATE
// ============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import webSocketService from '../services/websocketService';
import {
  computeEdgeFloodPrediction,
  computeFleetCentroid,
  fetchWeather,
  getCachedWeather,
  normalizeIncomingWeather,
  fetchServerWeather, // --- UPDATE (weather v2)
  reportRain as reportRainApi, // --- UPDATE (weather v2)
} from '../services/aiWeatherService';

const DEFAULTS = Object.freeze({
  enabled: true,
  weatherRefreshMs: 5 * 60 * 1000, // --- UPDATE (weather v2): was 10 min
  serverFreshMs: 30 * 60 * 1000,
  serverWeatherFreshMs: 60 * 60 * 1000,
  criticalCooldownMs: 10 * 60 * 1000,
});

const WS_WEATHER_EVENTS = ['weather_update', 'ai_weather_forecast'];
const CLOCK_MS = 60 * 1000;

const parseMeta = (meta) => {
  if (!meta) return {};
  if (typeof meta === 'object') return meta;
  try {
    return JSON.parse(meta) || {};
  } catch {
    return {};
  }
};

const toMs = (ts) => {
  if (ts === null || ts === undefined || ts === '') return null;
  const n = typeof ts === 'number' ? ts : Date.parse(ts);
  return Number.isFinite(n) ? (n < 1e12 ? n * 1000 : n) : null;
};

export const getRiskTier = (p) => {
  if (!p) return null;
  const prob = Math.round((Number(p.floodRiskProbability ?? p.flood_probability) || 0) * 100);
  const sev = String(p.predictedSeverity || p.flood_risk || '').toUpperCase();
  if (sev === 'CRITICAL' || prob >= 85) return 'critical';
  if (sev === 'HIGH' || prob >= 65) return 'high';
  if (sev === 'WARNING' || prob >= 40) return 'watch';
  return 'stable';
};

const materialKey = (p) =>
  p
    ? [
        p.floodRiskProbability, p.predictedSeverity, p.estimatedMinutesToOvertopping, p.rateOfRisePerMinute,
        p.confidence, p.trend, (p.probability_forecast || []).join(','), p.source, p.serverStale ? 1 : 0,
        (p.recommendations || []).length,
      ].join('|')
    : '';

export const useAiWeatherIntelligence = ({
  assets = [],
  sensors = [],
  waterLevelReadings = {},
  waterLevelHistory = {},
  serverPredictions = {},
  options = {},
  onNewCritical,
} = {}) => {
  const cfg = { ...DEFAULTS, ...options };
  const [polledWeather, setPolledWeather] = useState(null);
  const [serverWeather, setServerWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [clock, setClock] = useState(() => Date.now());
  const forceRef = useRef(false);
  const stableRef = useRef(new Map());
  const criticalSeenRef = useRef(new Map());
  const onNewCriticalRef = useRef(onNewCritical);
  onNewCriticalRef.current = onNewCritical;

  const wells = useMemo(
    () => (assets || []).filter((a) => String(a?.type || '').toLowerCase() === 'well'),
    [assets]
  );
  // --- UPDATE (weather v2): centre of every mapped asset (was: the wells with telemetry — often one well)
  const centroid = useMemo(() => computeFleetCentroid(assets && assets.length ? assets : wells), [wells, assets]);
  const serverDownUntilRef = useRef(0);
  // --- END UPDATE

  // ── clock (re-evaluates staleness / freshness) ──
  useEffect(() => {
    const id = setInterval(() => setClock(Date.now()), CLOCK_MS);
    return () => clearInterval(id);
  }, []);

  // ── weather polling ──
  useEffect(() => {
    if (!cfg.enabled) return undefined;
    let cancelled = false;
    const ttl = Math.max(60000, cfg.weatherRefreshMs);

    const load = (force, userForced = false) => { // --- UPDATE (weather v2): userForced → server recompute
      const cached = getCachedWeather(centroid.lat, centroid.lng, ttl);
      if (!force && cached && Date.now() < serverDownUntilRef.current) { // --- UPDATE (weather v2): the local cache only holds the fallback
        setPolledWeather(cached);
        setWeatherError(null);
        return;
      }
      setWeatherLoading(true);
      // --- UPDATE (weather v2): FlowSense server first; Open-Meteo direct only when the server can't answer
      const viaServer = Date.now() >= serverDownUntilRef.current
        ? fetchServerWeather(centroid.lat, centroid.lng, { force: userForced }).catch((err) => {
          serverDownUntilRef.current = Date.now() + 15 * 60 * 1000; // retry the server in 15 min
          if (process.env.NODE_ENV !== 'production') console.warn('[AiWeather] server weather unavailable, using Open-Meteo:', err?.message); // eslint-disable-line no-console
          return fetchWeather(centroid.lat, centroid.lng);
        })
        : fetchWeather(centroid.lat, centroid.lng);
      // --- END UPDATE
      viaServer
        .then((data) => {
          if (cancelled) return;
          setPolledWeather(data);
          setWeatherError(null);
        })
        .catch((err) => {
          if (!cancelled) setWeatherError(err);
        })
        .finally(() => {
          if (!cancelled) setWeatherLoading(false);
        });
    };

    load(forceRef.current, forceRef.current);
    forceRef.current = false;
    const id = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') load(true);
    }, ttl);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [cfg.enabled, cfg.weatherRefreshMs, centroid.lat, centroid.lng, reloadToken]);

  // ── server-pushed weather over WebSocket ──
  useEffect(() => {
    const handler = (event) => {
      const normalized = normalizeIncomingWeather(event);
      if (normalized) setServerWeather(normalized);
    };
    WS_WEATHER_EVENTS.forEach((evt) => webSocketService.subscribe(evt, handler));
    return () => WS_WEATHER_EVENTS.forEach((evt) => webSocketService.unsubscribe(evt, handler));
  }, []);

  const reloadWeather = useCallback(() => {
    forceRef.current = true;
    setReloadToken((t) => t + 1);
  }, []);

  // --- UPDATE (weather v2): the newest reading wins; a fresh staff report beats a model-only reading
  const weather = useMemo(() => {
    const fresh = [serverWeather, polledWeather].filter((w) => w && clock - (w.fetchedAt || 0) < cfg.serverWeatherFreshMs);
    if (!fresh.length) return polledWeather || serverWeather || null;
    const reported = fresh.filter((w) => w.observation && w.observation.fresh);
    const pool = reported.length ? reported : fresh;
    return pool.reduce((a, b) => ((b.fetchedAt || 0) > (a.fetchedAt || 0) ? b : a));
  }, [serverWeather, polledWeather, clock, cfg.serverWeatherFreshMs]);

  const [reporting, setReporting] = useState(false);
  const reportRain = useCallback(async (intensity, extra = {}) => {
    setReporting(true);
    try {
      const out = await reportRainApi({ intensity, ...extra });
      if (out?.now) setPolledWeather(out.now);
      return out;
    } finally {
      setReporting(false);
    }
  }, []);
  // --- END UPDATE

  // ── sensor index: well id → water-level sensors ──
  const sensorsByWell = useMemo(() => {
    const map = new Map();
    (sensors || []).forEach((s) => {
      if (!s) return;
      const keys = new Set();
      if (s.asset_id !== undefined && s.asset_id !== null) keys.add(String(s.asset_id));
      if (s.linked_entity_id !== undefined && s.linked_entity_id !== null) keys.add(String(s.linked_entity_id));
      keys.forEach((k) => {
        const list = map.get(k);
        if (list) list.push(s);
        else map.set(k, [s]);
      });
    });
    return map;
  }, [sensors]);

  // ── edge predictions ──
  const edgePredictions = useMemo(() => {
    const out = {};
    const nextStable = new Map();
    wells.forEach((well) => {
      const id = String(well.id);
      const linked = sensorsByWell.get(id) || [];
      let bestSensor = null;
      let bestTs = -Infinity;
      linked.forEach((s) => {
        const devId = s.device_id || s.id;
        const r = waterLevelReadings[devId];
        if (!r || r.level === undefined || r.level === null) return;
        const ts = toMs(r.timestamp) ?? 0;
        if (ts > bestTs) {
          bestTs = ts;
          bestSensor = devId;
        }
      });
      if (!bestSensor) return;
      const meta = parseMeta(well.metadata);
      const clearance = Number(meta.latest_clearance_score);
      const pred = computeEdgeFloodPrediction({
        history: waterLevelHistory[bestSensor] || [],
        reading: waterLevelReadings[bestSensor],
        weather,
        clearanceScore: Number.isFinite(clearance) ? clearance : null,
        now: clock,
      });
      if (!pred) return;
      pred.sensorId = bestSensor;
      const prev = stableRef.current.get(`edge:${id}`);
      const stable = prev && materialKey(prev) === materialKey(pred) ? prev : pred;
      nextStable.set(`edge:${id}`, stable);
      out[id] = stable;
    });
    stableRef.current.forEach((v, k) => {
      if (!k.startsWith('edge:')) nextStable.set(k, v);
    });
    stableRef.current = nextStable;
    return out;
  }, [wells, sensorsByWell, waterLevelReadings, waterLevelHistory, weather, clock]);

  // ── merge: server (fresh) > backend-enriched well.ai_predictions > edge ──
  const predictions = useMemo(() => {
    const out = {};
    const ids = new Set([
      ...Object.keys(serverPredictions || {}),
      ...Object.keys(edgePredictions),
      ...wells.filter((w) => w.ai_predictions).map((w) => String(w.id)),
    ]);
    const wellById = new Map(wells.map((w) => [String(w.id), w]));

    ids.forEach((id) => {
      const server = serverPredictions?.[id];
      const enriched = wellById.get(id)?.ai_predictions;
      const edge = edgePredictions[id];
      const receivedAt = toMs(server?.receivedAt ?? server?.updated_at);
      const serverFresh = server && (receivedAt === null || clock - receivedAt < cfg.serverFreshMs);

      let merged;
      if (serverFresh) {
        merged = {
          ...server,
          source: 'server',
          updated_at: server.updated_at ?? server.receivedAt ?? null,
          edgeCrossCheck: edge ? { probability: edge.floodRiskProbability, severity: edge.predictedSeverity } : null,
        };
      } else if (enriched) {
        merged = { ...enriched, source: enriched.source || 'server' };
      } else if (edge) {
        merged = server ? { ...edge, serverStale: true, staleServer: server } : edge;
      } else if (server) {
        merged = { ...server, source: 'server', serverStale: true };
      }
      if (!merged) return;

      const key = `merged:${id}`;
      const prev = stableRef.current.get(key);
      const stable = prev && prev.__base === (serverFresh ? server : enriched || edge || server) && materialKey(prev) === materialKey(merged)
        ? prev
        : Object.defineProperty(merged, '__base', { value: serverFresh ? server : enriched || edge || server, enumerable: false });
      stableRef.current.set(key, stable);
      out[id] = stable;
    });
    return out;
  }, [serverPredictions, edgePredictions, wells, clock, cfg.serverFreshMs]);

  // ── fleet insights ──
  const insights = useMemo(() => {
    const counts = { critical: 0, high: 0, watch: 0, stable: 0 };
    const ranked = [];
    let confSum = 0;
    let confN = 0;
    let server = 0;
    let edge = 0;
    let earliest = null;
    const wellById = new Map(wells.map((w) => [String(w.id), w]));
    Object.entries(predictions).forEach(([id, p]) => {
      const tier = getRiskTier(p);
      if (tier) counts[tier] += 1;
      if (p.source === 'edge') edge += 1;
      else server += 1;
      const conf = Number(p.confidence);
      if (Number.isFinite(conf)) {
        confSum += conf > 1 ? conf / 100 : conf;
        confN += 1;
      }
      const eta = Number(p.estimatedMinutesToOvertopping);
      const well = wellById.get(id) || { id, name: `Well ${id}` };
      if (p.estimatedMinutesToOvertopping !== null && p.estimatedMinutesToOvertopping !== undefined && Number.isFinite(eta)) {
        if (!earliest || eta < earliest.eta) earliest = { well, eta, prediction: p };
      }
      ranked.push({ well, prediction: p, tier, probability: Number(p.floodRiskProbability ?? p.flood_probability) || 0 });
    });
    ranked.sort((a, b) => b.probability - a.probability);

    const rain24 = Number(weather?.next24hRainMm) || 0;
    const atRisk = counts.critical + counts.high;
    let narrative;
    if (counts.critical > 0) {
      narrative = `${counts.critical} well(s) at critical overflow risk${earliest ? ` — earliest ${earliest.well.name || `Well ${earliest.well.id}`} in ~${earliest.eta} min` : ''}. Dispatch recommended.`;
    } else if (atRisk > 0) {
      narrative = `${atRisk} well(s) at elevated risk${rain24 >= 10 ? `; ${rain24} mm forecast rainfall will amplify rise` : ''}.`;
    } else if (rain24 >= 25) {
      narrative = `All wells stable, but ${rain24} mm rain is forecast in 24h — monitor low-clearance wells.`;
    } else if (ranked.length) {
      narrative = `All ${ranked.length} evaluated well(s) stable. Rainfall outlook ${rain24 >= 10 ? 'moderate' : 'benign'}.`;
    } else {
      narrative = 'Waiting for water-level telemetry to evaluate wells.';
    }

    return {
      counts,
      total: ranked.length,
      wellCount: wells.length,
      top: ranked.slice(0, 5),
      earliest,
      avgConfidence: confN ? confSum / confN : null,
      serverCount: server,
      edgeCount: edge,
      narrative,
    };
  }, [predictions, wells, weather]);

  // ── newly-critical notifications (deduped, with cooldown) ──
  useEffect(() => {
    if (!onNewCriticalRef.current) return;
    const nowMs = Date.now();
    const wellById = new Map(wells.map((w) => [String(w.id), w]));
    const fresh = [];
    Object.entries(predictions).forEach(([id, prediction]) => {
      if (getRiskTier(prediction) !== 'critical') return;
      const last = criticalSeenRef.current.get(id);
      if (last && nowMs - last <= cfg.criticalCooldownMs) return;
      criticalSeenRef.current.set(id, nowMs);
      fresh.push({ well: wellById.get(id) || { id, name: `Well ${id}` }, prediction, tier: 'critical' });
    });
    if (fresh.length) onNewCriticalRef.current(fresh);
  }, [predictions, wells, cfg.criticalCooldownMs]);

  return {
    weather,
    weatherStatus: {
      isLoading: weatherLoading,
      error: weatherError,
      source: weather?.source || null,
      fetchedAt: weather?.fetchedAt || null,
      isServerPushed: !!(serverWeather && weather === serverWeather),
      location: centroid,
      // --- UPDATE (weather v2)
      observation: weather?.observation || null,
      models: weather?.models || null,
      reporting,
      // --- END UPDATE
    },
    reloadWeather,
    reportRain, // --- UPDATE (weather v2)
    predictions,
    edgePredictions,
    insights,
  };
};

export default useAiWeatherIntelligence;
// --- END UPDATE